import { Api } from '@monorepo/shared/apiClient';
import {
  OrganizationDocumentsCacheEvents,
  organizationDocumentsCacheRegister,
} from '@monorepo/shared/cacheRegisters/documents/organizationDocumentsCacheRegister';
import {
  getKeyParamsForInvalidation,
  getQueryConfig,
} from '@monorepo/shared/utils/queryUtils';
import { DocumentFileResponse } from 'mapistry-shared';
import { queryCache, QueryConfig, useQuery } from 'react-query';

type UseOrganizationDocumentsListParams =
  Partial<Api.FetchOrganizationDocumentsParams> & {
    config?: QueryConfig<DocumentFileResponse[], Api.ErrorResponse>;
  };

const KEY_START = 'organization-documents-list';

export const createKey = ({
  organizationId,
  folderId,
}: Api.FetchOrganizationDocumentsParams) =>
  [KEY_START, organizationId, folderId] as const;

const fetcher: Api.DataHookQueryFn<
  typeof createKey,
  typeof Api.fetchOrganizationDocuments
> = (_key, organizationId, folderId) =>
  Api.fetchOrganizationDocuments({ organizationId, folderId });

export function useOrganizationDocumentsList({
  organizationId,
  folderId,
  config: inputConfig,
}: UseOrganizationDocumentsListParams) {
  const isEnabled = !!organizationId && !!folderId;
  const config = getQueryConfig(inputConfig, isEnabled);
  const key = isEnabled ? createKey({ organizationId, folderId }) : undefined;
  const { data, ...queryInfo } = useQuery(key, fetcher, config);

  return { ...queryInfo, documents: data };
}

type InvalidateCacheParams = {
  organizationId: string;
  folderId?: string;
};

export async function invalidateCache(
  keyParams: InvalidateCacheParams,
): Promise<void> {
  await queryCache.invalidateQueries(
    getKeyParamsForInvalidation([
      KEY_START,
      keyParams.organizationId,
      keyParams.folderId,
    ]),
  );
}

organizationDocumentsCacheRegister(
  [
    OrganizationDocumentsCacheEvents.CREATE,
    OrganizationDocumentsCacheEvents.UPDATE,
    OrganizationDocumentsCacheEvents.DELETE,
    OrganizationDocumentsCacheEvents.MOVE,
  ],
  {
    hookName: 'useOrganizationDocumentsList',
    callback: (keyParams) => invalidateCache(keyParams),
  },
);
